import type {
  AutoRuleStatisticsActionGroup,
  AutoRuleStatisticsCondition,
  AutoRuleStatisticsCoverageItem,
  AutoRuleStatisticsExecutionItem,
  AutoRuleStatisticsOverview,
  AutoRuleStatisticsOverviewCondition,
  AutoRuleStatisticsPageResult,
  AutoRuleStatisticsRuleItem,
  AutoRuleStatisticsTargetItem,
  AutoRuleStatisticsUncoveredItem,
  AutoRuleStatisticsUserItem,
} from './statistics-types'

import { defineApi } from '#/api/define-api'
import { requestClient } from '#/api/request'

interface ListData<T> extends Recordable {
  list?: T[]
  total?: number
}

interface ExecutionCondition extends AutoRuleStatisticsCondition {
  adAccountId?: string
  adId?: string
  adSetId?: string
  campaignId?: string
  dateEnd: string
  dateStart: string
  mediaPlatform?: string
  timezone?: string
}

interface StatisticsPageRequest<T> {
  condition: T
  orderBy?: 'asc' | 'desc'
  pageIndex: number
  pageSize: number
  sortBy?: string
}

function pageRequest<T>(params: StatisticsPageRequest<T>) {
  return {
    condition: params.condition,
    orderBy: params.orderBy,
    pageIndex: params.pageIndex,
    pageSize: params.pageSize,
    sortBy: params.sortBy,
  }
}

export const autoRuleStatisticsApi = defineApi('operation', ({ prefix }) => {
  /** 查询自动规则统计概览。POST /adsAutoRule/statistics/overview */
  function queryOverview(condition?: AutoRuleStatisticsOverviewCondition) {
    return requestClient
      .post<AutoRuleStatisticsOverview>(
        prefix('adsAutoRule/statistics/overview'),
        { condition },
      )
      .then((res) => res)
  }

  /** 分页查询规则统计明细。POST /adsAutoRule/statistics/rule/page */
  function queryRules(
    params: StatisticsPageRequest<AutoRuleStatisticsCondition>,
  ): Promise<AutoRuleStatisticsPageResult<AutoRuleStatisticsRuleItem>> {
    return requestClient
      .post<
        ListData<AutoRuleStatisticsRuleItem>
      >(prefix('adsAutoRule/statistics/rule/page'), pageRequest(params))
      .then((res) => ({
        items: res?.list ?? [],
        total: res?.total,
      }))
  }

  /** 按动作分类统计规则。POST /adsAutoRule/statistics/action */
  function queryActionGroups(condition?: AutoRuleStatisticsCondition) {
    return requestClient
      .post<
        ListData<AutoRuleStatisticsActionGroup>
      >(prefix('adsAutoRule/statistics/action'), { condition })
      .then((res) => res?.list ?? [])
  }

  /** 按监控层级统计规则。POST /adsAutoRule/statistics/target */
  function queryTargetGroups(condition?: AutoRuleStatisticsCondition) {
    return requestClient
      .post<
        ListData<AutoRuleStatisticsTargetItem>
      >(prefix('adsAutoRule/statistics/target'), { condition })
      .then((res) => res?.list ?? [])
  }

  /** 分页查询每日执行统计。POST /adsAutoRule/statistics/execution/page */
  function queryExecutions(
    params: StatisticsPageRequest<ExecutionCondition>,
  ): Promise<AutoRuleStatisticsPageResult<AutoRuleStatisticsExecutionItem>> {
    return requestClient
      .post<
        ListData<AutoRuleStatisticsExecutionItem>
      >(prefix('adsAutoRule/statistics/execution/page'), pageRequest(params))
      .then((res) => ({
        items: res?.list ?? [],
        total: res?.total,
      }))
  }

  /** 查询规则覆盖统计。POST /adsAutoRule/statistics/coverage */
  function queryCoverage(condition?: AutoRuleStatisticsCondition) {
    return requestClient
      .post<{
        list?: AutoRuleStatisticsCoverageItem[]
      }>(prefix('adsAutoRule/statistics/coverage'), { condition })
      .then((res) => res?.list ?? [])
  }

  /** 分页查询未覆盖对象及指标。POST /adsAutoRule/statistics/uncovered/page */
  function queryUncovered(
    params: StatisticsPageRequest<AutoRuleStatisticsCondition>,
  ): Promise<AutoRuleStatisticsPageResult<AutoRuleStatisticsUncoveredItem>> {
    return requestClient
      .post<
        ListData<AutoRuleStatisticsUncoveredItem>
      >(prefix('adsAutoRule/statistics/uncovered/page'), pageRequest(params))
      .then((res) => ({
        items: res?.list ?? [],
        total: res?.total,
      }))
  }

  /** 分页查询客户用户规则统计。POST /adsAutoRule/statistics/user/page */
  function queryUsers(
    params: StatisticsPageRequest<AutoRuleStatisticsCondition>,
  ): Promise<AutoRuleStatisticsPageResult<AutoRuleStatisticsUserItem>> {
    return requestClient
      .post<
        ListData<AutoRuleStatisticsUserItem>
      >(prefix('adsAutoRule/statistics/user/page'), pageRequest(params))
      .then((res) => ({
        items: res?.list ?? [],
        total: res?.total,
      }))
  }

  return {
    queryActionGroups,
    queryCoverage,
    queryExecutions,
    queryOverview,
    queryRules,
    queryTargetGroups,
    queryUncovered,
    queryUsers,
  }
})
